import { apiClient } from './client';
import { EnneagramType } from '../../types/enneagram';

const defaultEnneagramTypes: Record<number, EnneagramType> = {
  1: {
    number: 1,
    name: "The Reformer",
    nickname: "The Perfectionist",
    description: "Ethical, dedicated and reliable, ones are motivated by a desire to live the right way, improve the world, and avoid fault and blame.",
    strengths: [
      "Strong sense of purpose",
      "Ethical and moral",
      "Organized and structured",
      "Improvement-oriented"
    ],
    challenges: [
      "Excessive self-criticism",
      "Difficulty relaxing",
      "Tendency to judge others",
      "Perfectionism"
    ],
    growthPath: "Learning to accept imperfection and finding peace with what is, rather than what should be."
  },
  2: {
    number: 2,
    name: "The Helper",
    nickname: "The Giver",
    description: "Warm, caring and giving, twos are motivated by a need to be loved and needed, and to avoid acknowledging their own needs.",
    strengths: [
      "Empathetic and compassionate",
      "Generous and self-sacrificing",
      "People-oriented",
      "Caring and warm"
    ],
    challenges: [
      "Difficulty acknowledging own needs",
      "Tendency to manipulate",
      "Dependency on others' approval",
      "Pride in their giving"
    ],
    growthPath: "Learning to acknowledge and meet their own needs while maintaining healthy boundaries with others."
  },
  3: {
    number: 3,
    name: "The Achiever",
    nickname: "The Performer",
    description: "Success-oriented, pragmatic, and adaptable, threes are motivated by a need to be productive, to achieve success, and to avoid failure.",
    strengths: [
      "Ambitious and driven",
      "Efficient and practical",
      "Adaptable and excellent",
      "Goal-oriented"
    ],
    challenges: [
      "Image consciousness",
      "Workaholic tendencies",
      "Competitive nature",
      "Fear of failure"
    ],
    growthPath: "Learning to value themselves for who they are, not just for their achievements and success."
  },
  4: {
    number: 4,
    name: "The Individualist",
    nickname: "The Romantic",
    description: "Creative, sensitive, and expressive, fours are motivated by a need to be unique, to find meaning, and to avoid being ordinary.",
    strengths: [
      "Creative and expressive",
      "Emotionally honest",
      "Introspective",
      "Compassionate"
    ],
    challenges: [
      "Melancholy and moodiness",
      "Self-absorption",
      "Feeling misunderstood",
      "Envy of others"
    ],
    growthPath: "Learning to find contentment and value in the present moment rather than longing for what's missing."
  },
  5: {
    number: 5,
    name: "The Investigator",
    nickname: "The Observer",
    description: "Perceptive, innovative, and isolated, fives are motivated by a need to gain knowledge, to conserve energy, and to avoid depending on others.",
    strengths: [
      "Analytical mind",
      "Objective perspective",
      "Independent thinking",
      "Intellectual depth"
    ],
    challenges: [
      "Detachment from emotions",
      "Social isolation",
      "Hoarding resources",
      "Overthinking"
    ],
    growthPath: "Learning to engage with the world and others rather than withdrawing into the mind."
  },
  6: {
    number: 6,
    name: "The Loyalist",
    nickname: "The Guardian",
    description: "Committed, security-oriented, and engaging, sixes are motivated by fear and the need for security, guidance, and support.",
    strengths: [
      "Loyal and committed",
      "Responsible",
      "Problem-solving ability",
      "Cooperative"
    ],
    challenges: [
      "Anxiety and worry",
      "Doubt and questioning",
      "Fear of authority",
      "Worst-case thinking"
    ],
    growthPath: "Learning to trust themselves and develop inner guidance rather than seeking it externally."
  },
  7: {
    number: 7,
    name: "The Enthusiast",
    nickname: "The Adventurer",
    description: "Busy, fun-loving, and spontaneous, sevens are motivated by a need to be happy, to plan stimulating experiences, and to avoid pain.",
    strengths: [
      "Optimistic outlook",
      "Versatile abilities",
      "Quick thinking",
      "Adventure-seeking"
    ],
    challenges: [
      "Difficulty with commitment",
      "Scattered focus",
      "Avoiding pain",
      "Excessive planning"
    ],
    growthPath: "Learning to find satisfaction in the present moment and face difficult emotions rather than escaping into planning."
  },
  8: {
    number: 8,
    name: "The Challenger",
    nickname: "The Protector",
    description: "Powerful, dominating, and self-confident, eights are motivated by a need to be strong and avoid feeling weak or vulnerable.",
    strengths: [
      "Strong leadership",
      "Protective of others",
      "Direct communication",
      "Decision-making ability"
    ],
    challenges: [
      "Excessive control",
      "Confrontational style",
      "Difficulty with vulnerability",
      "Intimidating presence"
    ],
    growthPath: "Learning to balance power with vulnerability and to express love and tenderness."
  },
  9: {
    number: 9,
    name: "The Peacemaker",
    nickname: "The Mediator",
    description: "Easy-going, self-effacing, and supportive, nines are motivated by a need to keep the peace, merge with others, and avoid conflict.",
    strengths: [
      "Natural mediator",
      "Accepting of others",
      "Calming presence",
      "Inclusive perspective"
    ],
    challenges: [
      "Conflict avoidance",
      "Self-forgetting",
      "Procrastination",
      "Stubborn resistance"
    ],
    growthPath: "Learning to assert themselves and recognize the value of their own presence and opinions."
  }
};

export const enneagramTypesApi = {
  getEnneagramTypes: async (): Promise<Record<number, EnneagramType>> => {
    try {
      // Try to get from API
      const response = await apiClient.get('/enneagram-types');
      return response.data;
    } catch (error) {
      console.error('Error fetching Enneagram types, using defaults:', error);
      // Fallback to default data if API fails
      return defaultEnneagramTypes;
    }
  },

  getTypeById: async (id: number): Promise<EnneagramType | null> => {
    try {
      // Try to get from API
      const response = await apiClient.get(`/enneagram-types/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching Enneagram type ${id}, using defaults:`, error);
      // Fallback to default data if API fails
      return defaultEnneagramTypes[id] || null;
    }
  }
};
